import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {RequestStatus} from '../../utils/Enums';
import {apiClient} from '../../utils/ApiClient';
import {VendorContact} from '../../models/VendorContact';
import {ModifyVendorContactFormProps} from "../../components/vendorContacts/ModifyVendorContactScreen";
import {VendorContactRating} from "../../models/VendorContactRating";

interface VendorContactsState {
    vendorContacts: VendorContact[];
    currentVendorContact: VendorContact | null;
    getVendorContactsStatus: RequestStatus;
    createVendorContactStatus: RequestStatus;
    updateVendorContactStatus: RequestStatus;
    deleteVendorContactStatus: RequestStatus;
    getCurrentVendorContactStatus: RequestStatus;
    getVendorContactRatingsStatus: RequestStatus;
    errors: string[];
    vendorId: number | null;
    ratings: VendorContactRating[];
}

const initialState: VendorContactsState = {
    vendorContacts: [],
    currentVendorContact: null,
    getVendorContactsStatus: RequestStatus.Idle,
    createVendorContactStatus: RequestStatus.Idle,
    updateVendorContactStatus: RequestStatus.Idle,
    deleteVendorContactStatus: RequestStatus.Idle,
    getCurrentVendorContactStatus: RequestStatus.Idle,
    getVendorContactRatingsStatus: RequestStatus.Idle,
    errors: [],
    vendorId: null,
    ratings: []
};

export const getVendorContacts = createAsyncThunk(
    'vendorContacts/getVendorContacts',
    async ({ vendorId }: { vendorId: number }, { rejectWithValue }) => {
        const response = await apiClient.get(`vendors/${vendorId}/contacts`);
        return response.data;
    }
);

export const createVendorContact = createAsyncThunk(
    'vendorContacts/createVendorContact',
    async ({ vendorId, vendorContactData }: { vendorId: number; vendorContactData: any }, { rejectWithValue }) => {
        const response = await apiClient.post(`vendors/${vendorId}/contacts/store`, vendorContactData);
        return response.data;
    }
);

export const updateVendorContact = createAsyncThunk(
    'vendorContacts/updateVendorContact',
    async ({ vendorId, contactId, vendorContactData }: { vendorId: number; contactId: number, vendorContactData: ModifyVendorContactFormProps }, { rejectWithValue }) => {
        const response = await apiClient.put(`vendors/${vendorId}/contacts/${contactId}`, vendorContactData);
        return response.data;
    }
);

export const deleteVendorContact = createAsyncThunk(
    'vendorContacts/deleteVendorContact',
    async ({ vendorId, contactId, replacements }: { vendorId: number, contactId: number, replacements: { [key: string]: { startCountryId: number; destinationCountryId: number; replacementId: number | null; } } }, { rejectWithValue }) => {
        const response = await apiClient.put(`vendors/${vendorId}/contacts/${contactId}/delete`, { replacements });
        return response.data;
    }
);

export const getCurrentVendorContact = createAsyncThunk(
    'vendorContacts/getCurrentVendorContact',
    async ({ vendorId, contactId }: { vendorId: number, contactId: number }, { rejectWithValue }) => {
        const response = await apiClient.get(`vendors/${vendorId}/contacts/${contactId}`);
        return response.data;
    }
);

export const getVendorContactRatings = createAsyncThunk(
    'vendorContacts/getVendorContactRatings',
    async ({ vendorId, contactId }: { vendorId: number, contactId: number }, { rejectWithValue }) => {
        const response = await apiClient.get(`vendors/${vendorId}/contacts/${contactId}/ratings`);
        return response.data;
    }
);

const vendorContactsSlice = createSlice({
    name: 'vendorContacts',
    initialState,
    reducers: {
        resetCreateVendorContactStatus: (state) => {
            state.createVendorContactStatus = RequestStatus.Idle;
            state.currentVendorContact = null;
        },
        resetUpdateVendorContactStatus: (state) => {
            state.updateVendorContactStatus = RequestStatus.Idle;
            state.currentVendorContact = null;
        },
        resetDeleteVendorContactStatus: (state) => {
            state.deleteVendorContactStatus = RequestStatus.Idle;
            state.currentVendorContact = null;
        },
        setVendorId: (state, action) => {
            state.vendorId = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getVendorContacts.pending, (state) => {
                state.getVendorContactsStatus = RequestStatus.Loading;
            })
            .addCase(getVendorContacts.fulfilled, (state, action) => {
                state.getVendorContactsStatus = RequestStatus.Success;
                state.vendorContacts = action.payload;
            })
            .addCase(getVendorContacts.rejected, (state) => {
                state.getVendorContactsStatus = RequestStatus.Failed;
            })
            .addCase(createVendorContact.pending, (state) => {
                state.createVendorContactStatus = RequestStatus.Loading;
            })
            .addCase(createVendorContact.fulfilled, (state) => {
                state.createVendorContactStatus = RequestStatus.Success;
            })
            .addCase(createVendorContact.rejected, (state, action) => {
                state.createVendorContactStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })
            .addCase(updateVendorContact.pending, (state) => {
                state.updateVendorContactStatus = RequestStatus.Loading;
            })
            .addCase(updateVendorContact.fulfilled, (state) => {
                state.updateVendorContactStatus = RequestStatus.Success;
            })
            .addCase(updateVendorContact.rejected, (state, action) => {
                state.updateVendorContactStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })
            .addCase(deleteVendorContact.pending, (state) => {
                state.deleteVendorContactStatus = RequestStatus.Loading;
            })
            .addCase(deleteVendorContact.fulfilled, (state) => {
                state.deleteVendorContactStatus = RequestStatus.Success;
            })
            .addCase(deleteVendorContact.rejected, (state, action)=> {
                state.deleteVendorContactStatus = RequestStatus.Failed;

                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }

            })
            .addCase(getCurrentVendorContact.pending, (state) => {
                state.getCurrentVendorContactStatus = RequestStatus.Loading;
            })
            .addCase(getCurrentVendorContact.fulfilled, (state, action) => {
                state.currentVendorContact = action.payload;
                state.getCurrentVendorContactStatus = RequestStatus.Success;
            })
            .addCase(getCurrentVendorContact.rejected, (state) => {
                state.getCurrentVendorContactStatus = RequestStatus.Failed;
            })

            .addCase(getVendorContactRatings.pending, (state) => {
                state.getVendorContactRatingsStatus = RequestStatus.Loading;
            })
            .addCase(getVendorContactRatings.fulfilled, (state, action) => {
                state.ratings = action.payload;
                state.getVendorContactRatingsStatus = RequestStatus.Success;
            })
            .addCase(getVendorContactRatings.rejected, (state) => {
                state.getVendorContactRatingsStatus = RequestStatus.Failed;
            });
    },
});

export const {
    resetCreateVendorContactStatus,
    resetUpdateVendorContactStatus,
    resetDeleteVendorContactStatus,
} = vendorContactsSlice.actions;

export default vendorContactsSlice.reducer;